import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { resolveAssetUrl } from "../lib/api";
import {
  Tv, Heart, History, LogOut, LogIn, UserPlus, Shield, Sparkles,
  MessageCircle, Radio, ChevronDown, User,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const linkClass = ({ isActive }) =>
  `px-4 py-2 rounded-full border-4 border-slate-900 font-black uppercase text-sm tracking-wide transition-all hover-press ${
    isActive ? "bg-yellow-300 text-slate-900" : "bg-white text-slate-900"
  }`;

// Row inside the account dropdown — matches the retro neon aesthetic.
function AccountMenuItem({ to, onSelect, icon: Icon, label, testid, tone = "default" }) {
  const toneCls =
    tone === "danger"
      ? "hover:bg-pink-500 hover:text-white focus:bg-pink-500 focus:text-white"
      : "hover:bg-yellow-300 hover:text-slate-900 focus:bg-yellow-300 focus:text-slate-900";
  const inner = (
    <span className="flex items-center gap-2 font-black uppercase text-sm tracking-wide">
      <Icon className="w-4 h-4" strokeWidth={3} />
      {label}
    </span>
  );
  if (onSelect) {
    return (
      <DropdownMenuItem
        onSelect={onSelect}
        data-testid={testid}
        className={`cursor-pointer px-3 py-2 rounded-lg text-white ${toneCls}`}
      >
        {inner}
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem
      asChild
      data-testid={testid}
      className={`cursor-pointer px-3 py-2 rounded-lg text-white ${toneCls}`}
    >
      <Link to={to}>{inner}</Link>
    </DropdownMenuItem>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const showMarquee = location.pathname === "/";
  const isLoggedIn = !!(user && user !== false);

  return (
    <nav className="sticky top-0 z-50 bg-[#0a0f0d]/90 backdrop-blur-md border-b-4 border-[#ff2fa7] shadow-[0_1px_8px_rgba(57,255,20,0.14)]" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
        <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <div className="bg-pink-500 border-4 border-slate-900 rounded-2xl p-2 shadow-brutal-sm group-hover:rotate-[-6deg] transition-transform">
            <Tv className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <div className="font-display font-black text-2xl sm:text-3xl text-white text-bubble-sm">
            DESENE<span className="text-yellow-300">2000</span>
          </div>
        </Link>

        <div className="flex-1" />

        <div className="flex items-center gap-2 flex-wrap">
          {/* Primary navigation — always visible */}
          <NavLink to="/" end className={linkClass} data-testid="nav-home">
            Acasă
          </NavLink>
          <NavLink to="/seriale" className={linkClass} data-testid="nav-series">
            Seriale
          </NavLink>
          <NavLink to="/grila-tv" className={linkClass} data-testid="nav-tv-guide">
            <span className="inline-flex items-center gap-1">
              <Radio className="w-4 h-4" strokeWidth={3} /> Grila TV
            </span>
          </NavLink>

          {isLoggedIn ? (
            /* Logged-in state: avatar dropdown with every user-specific link */
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-account-trigger"
                  className="flex items-center gap-2 pr-3 pl-1 py-1 rounded-full border-4 border-slate-900 bg-white text-slate-900 font-black uppercase text-sm tracking-wide hover-press focus:outline-none data-[state=open]:bg-yellow-300"
                  title="Contul tău"
                >
                  <span className="w-7 h-7 rounded-full border-2 border-slate-900 overflow-hidden flex items-center justify-center bg-pink-500 text-white text-xs">
                    {user.avatar_url ? (
                      <img src={resolveAssetUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (user.name || "?").charAt(0).toUpperCase()
                    )}
                  </span>
                  <span className="hidden sm:inline max-w-[100px] truncate">{user.name || "Cont"}</span>
                  <ChevronDown className="w-4 h-4" strokeWidth={3} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={10}
                data-testid="nav-account-menu"
                className="min-w-[220px] bg-[#0a0f0d]/95 backdrop-blur-md border-4 border-[#ff2fa7] rounded-2xl p-2 shadow-[0_8px_24px_rgba(255,47,167,0.35)]"
              >
                <DropdownMenuLabel className="px-3 py-1 text-[10px] tracking-widest text-[#00e5ff] uppercase font-black">
                  Contul tău
                </DropdownMenuLabel>
                <AccountMenuItem
                  to="/profil"
                  icon={User}
                  label="Profil"
                  testid="nav-profile"
                />
                <AccountMenuItem
                  to="/chat"
                  icon={MessageCircle}
                  label="Chat"
                  testid="nav-chat"
                />
                <AccountMenuItem
                  to="/favorite"
                  icon={Heart}
                  label="Favorite"
                  testid="nav-favorites"
                />
                <AccountMenuItem
                  to="/istoric"
                  icon={History}
                  label="Istoric"
                  testid="nav-history"
                />
                {user.role === "admin" && (
                  <>
                    <DropdownMenuSeparator className="my-1 bg-[#ff2fa7]/40 h-[2px]" />
                    <AccountMenuItem
                      to="/admin"
                      icon={Shield}
                      label="Admin"
                      testid="nav-admin"
                    />
                  </>
                )}
                <DropdownMenuSeparator className="my-1 bg-[#ff2fa7]/40 h-[2px]" />
                <AccountMenuItem
                  onSelect={async () => { await logout(); navigate("/"); }}
                  icon={LogOut}
                  label="Ieșire"
                  testid="nav-logout-btn"
                  tone="danger"
                />
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            /* Logged-out state: keep Intră / Cont Nou visible */
            <>
              <NavLink to="/autentificare" className={linkClass} data-testid="nav-login">
                <span className="inline-flex items-center gap-1">
                  <LogIn className="w-4 h-4" strokeWidth={3} /> Intră
                </span>
              </NavLink>
              <NavLink
                to="/inregistrare"
                className="px-4 py-2 rounded-full border-4 border-slate-900 bg-lime-400 text-slate-900 font-black uppercase text-sm tracking-wide hover-press inline-flex items-center gap-1"
                data-testid="nav-register"
              >
                <UserPlus className="w-4 h-4" strokeWidth={3} /> Cont Nou
              </NavLink>
            </>
          )}
        </div>
      </div>

      {/* Bottom marquee — only on the homepage */}
      {showMarquee && (
        <div className="bg-pink-500 border-t-4 border-slate-900 overflow-hidden">
        <div className="animate-marquee flex gap-8 whitespace-nowrap py-1 text-white font-pixel text-xl">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={`marquee-${k}`} className="flex gap-8 px-4">
              <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> RETRĂIEȘTE COPILĂRIA</span>
              <span>★ Desenele anilor 2000 ★</span>
              <span>★ DESENE DUBLATE ★</span>
              <span>★ DESENE SUBTITRATE ★</span>
              <span>★ NICKELODEON ★</span>
              <span>★ CARTOON NETWORK ★</span>
              <span>★ FOX KIDS ★</span>
              <span>★ JETIX ★</span>
              <span>★ KIDS CO. ★</span>
              <span>★ MINIMAX ★</span>
              <span>★ BOOMERANG ★</span>
              <span>★ NOSTALGIE ★</span>
              <span>★ NON-STOP ★</span>
            </div>
          ))}
        </div>
      </div>
      )}
    </nav>
  );
}
